import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldPath, RegisterOptions, UseFormReturn, useForm, useWatch } from "react-hook-form";

import { formatCurrency } from "../../core/currency.core";
import posProductCore from "../../core/product.core";
import useRetailShop from "../../hooks/useRetailShop";
import { CustomProductData, CustomSuggestion, CustomSuggestionOption } from "../../types/posProduct";
import { Product } from "../../types/product";
import { CustomCartItem, CustomCartItemData } from "../../types/cart";
import useCart from "../../hooks/services/useCart.hook";
import { useTranslation } from "../../core/translation.core";

interface CustomProductProps {
  attachCartItemId?: string;
  customProduct: Product;
  onProductAdded: VoidFunction;
}
export default function CustomProduct({ customProduct, attachCartItemId, onProductAdded }: CustomProductProps) {
  const queryClient = useQueryClient();
  const [inCart, setInCart] = useState<CustomCartItem>();
  const { addCustomItem, getProductWarehouseId } = useCart();

  const { data: customSuggestions, isFetched } = useQuery({
    queryFn: (context) => posProductCore.getCustomSuggestions(context.queryKey[1]),
    queryKey: ["CustomSuggestions", customProduct.id],
  });

  async function addCart(data: CustomProductData) {
    if (!inCart) {
      //remove empty string
      data.options = data.options.filter((v) => v.value);
      data.topUps = data.topUps.filter((b) => b.name.length > 1 && Number(b.price) > 1);
      const totalPrice = data.topUps.reduce((r, n) => r + Number(n.price), Number(data.price));
      const queryObj: CustomCartItemData = {
        attachCartItemId,
        customization: { options: data.options, price: data.price },
        options: data.options,
        price: totalPrice,
        product: customProduct,
        quantity: 1,
        topUps: data.topUps.map((t) => ({ name: { en: t.name, zhHant: t.name }, price: t.price })),
        warehouseId: getProductWarehouseId()!,
      };
      addCustomItem(queryObj);
      setInCart(queryObj);
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      onProductAdded();
    } else {
      return;
    }
  }

  if (!isFetched) return null;
  if (!customSuggestions) return null;

  return <CustomProductSelector customSuggestions={customSuggestions} customProduct={customProduct} addCart={addCart} />;
}

interface CustomProductSelectorProps {
  customProduct: Product;
  customSuggestions: CustomSuggestion[];
  addCart: (data: CustomProductData) => void;
}

function CustomProductSelector({ customProduct, customSuggestions, addCart }: CustomProductSelectorProps) {
  const { t, msg } = useTranslation();
  const pos = useRetailShop();
  const methods = useForm<CustomProductData>({
    defaultValues: {
      //store dateTime as unique id in the hook form for react keys. should be stable across rerenders
      options: [{ groupLabel: getLabelAtIdx(customSuggestions, 0), id: new Date().getTime().toString(), value: "" }],
      price: undefined,
      topUps: [{ name: "", price: undefined }],
    },
  });

  const [options, price, topUps] = useWatch({ control: methods.control, name: ["options", "price", "topUps"] });
  const topUpsPrice = topUps.reduce((r, v) => Math.max(Number(v.price ?? 0), 0) + r, 0);
  const optionsPrice = Number(price) || 0;

  if (!pos) return null;

  return (
    <form onSubmit={methods.handleSubmit(addCart)} className="w-full">
      <section className="lg:grid lg:grid-cols-2 lg:gap-6">
        <CustomSuggestions methods={methods} customSuggestions={customSuggestions} />
        <TopUpOptions methods={methods} customProduct={customProduct} />
      </section>
      <p className="text-right text-2xl md:text-3xl mb-3 md:mb-6" id="custom-prod-price">
        {t(msg.cart.totalPrice)}: {formatCurrency(topUpsPrice + optionsPrice, pos.defaultCurrency)}
      </p>
      <Button isActive={optionsIsValid({ options, price })} id="custom-prod-submit" type="submit">
        {t(msg.form.confirm)}
      </Button>
    </form>
  );
}

const getLabelAtIdx = (customSuggestions: CustomSuggestion[], idx: number) => {
  const labelAtIdx0 = customSuggestions.map((cs) => cs.options[idx]?.groupLabel) || [];
  return labelAtIdx0.find((i) => i !== `option${idx + 1}`) || `option${idx + 1}`;
};

const optionsIsValid = ({ options, price }: { options: CustomSuggestionOption[]; price: number }) => {
  return options.some((o) => o.groupLabel.length > 1 && o.value.length > 1) && price > 0;
};

interface CustomSuggestionsProps {
  customSuggestions: CustomSuggestion[];
  methods: UseFormReturn<CustomProductData>;
}

function CustomSuggestions({ customSuggestions, methods }: CustomSuggestionsProps) {
  const { t, msg } = useTranslation();
  const selectedOptions = useWatch({ control: methods.control, name: "options" });

  useEffect(() => {
    const matchingSuggestions = getMatchingSuggestions({ customSuggestions, selectedOptions });
    if (matchingSuggestions?.length > 0) {
      methods.setValue("price", matchingSuggestions[0]?.price);
    }
  }, [selectedOptions]);

  function addRow() {
    methods.setValue("options", [
      ...selectedOptions,
      {
        groupLabel: getLabelAtIdx(customSuggestions, selectedOptions.length),
        id: new Date().getTime().toString(),
        value: "",
      },
    ]);
  }

  return (
    <section className="mb-4" id="custom-product">
      <h2 className="text-xl mb-3">{t(msg.product.groupItem)}</h2>
      <div>
        {selectedOptions.map((i, idx) => (
          <CustomOptionRow suggestion={i} key={i.id} idx={idx} customSuggestions={customSuggestions} methods={methods} />
        ))}
        <div className="flex gap-2">
          <div className="basis-1/4 shrink-0"></div>
          <Button id="add-custom-option-btn" onClick={addRow} type="button">
            {t(msg.product.addCustomOption)} <PlusIcon className="w-4 h-4 stroke-white stroke-2 fill-white" />
          </Button>
        </div>
        <div className="w-full flex justify-center mb-3">
          <label className="block w-full">
            <span className="font-medium">{t(msg.product.price2)}</span>
            <Input methods={methods} fieldName="price" registerOptions={{ required: true }} placeholder={t(msg.product.price2)} />
          </label>
        </div>
      </div>
    </section>
  );
}

// as suggestion options are being entered we want to use this to check for matches so we can autopopulate the price
const getMatchingSuggestions = ({
  customSuggestions,
  selectedOptions,
}: {
  customSuggestions: CustomSuggestion[];
  selectedOptions: CustomSuggestionOption[];
}) => {
  const foundObjectInArr = (obj: CustomSuggestionOption, array: CustomSuggestionOption[]) => {
    return array.find((i) => i.value === obj.value && i.groupLabel === obj.groupLabel);
  };

  return customSuggestions?.filter((cs) => cs.options.every((o) => foundObjectInArr(o, selectedOptions)));
};

interface CustomSuggestionsRowProps extends CustomSuggestionsProps {
  idx: number;
  suggestion: CustomSuggestionOption;
}

function CustomOptionRow({ idx, suggestion, customSuggestions, methods }: CustomSuggestionsRowProps) {
  const { t, msg } = useTranslation();

  if (!customSuggestions) return null;

  const matchingSuggestions = customSuggestions.map((o) => {
    const foundGroup = o.options.find((p) => p.groupLabel === suggestion.groupLabel);
    return foundGroup ? foundGroup.value : "";
  });

  const allGroupLabels = customSuggestions.flatMap((cs) => cs.options.map((o) => o.groupLabel));
  const uniqueSuggestions = Array.from(new Set(matchingSuggestions)).map((v) => ({ name: v, value: v }));

  // console.log("uniqueSuggestions", uniqueSuggestions);
  const uniqueSuggestionsLabel = Array.from(new Set(allGroupLabels)).map((gl) => ({ name: gl, value: gl }));

  const handleClick = (value: string) => {
    methods.setValue(`options.${idx}.value`, value);
  };

  const handleLabelClick = (value: string) => {
    methods.setValue(`options.${idx}.groupLabel`, value);
  };

  return (
    <div className="relative bg-inherit w-full mb-2 flex items-center gap-2">
      <div className="basis-1/4 shrink-0 font-medium">
        <SearchInput
          methods={methods}
          fieldName={`options.${idx}.groupLabel`}
          placeholder=""
          suggesstions={uniqueSuggestionsLabel}
          handleClick={handleLabelClick}
        />
      </div>

      <SearchInputSuggestionValue
        idx={idx}
        methods={methods}
        fieldName={`options.${idx}.value`}
        placeholder={t(msg.product.groupItem)}
        customSuggestions={customSuggestions}
        uniqueSuggestions={uniqueSuggestions}
        handleClick={handleClick}
      />
    </div>
  );
}

interface TopUpSuggestion {
  name: string;
  price?: number;
}

interface TopUpOptionsProps {
  methods: UseFormReturn<CustomProductData>;
  customProduct: Product;
}

function TopUpOptions({ methods, customProduct }: TopUpOptionsProps) {
  const { t, msg } = useTranslation();
  const { data: topUpSuggestions } = useQuery({
    queryFn: (context) => posProductCore.getTopUpSuggestions(context.queryKey[1]),
    queryKey: ["TopUpSuggestions", customProduct.id],
  });

  const topUps = useWatch({ control: methods.control, name: "topUps" });

  function addRow() {
    methods.setValue("topUps", [...topUps, { name: "", price: undefined }]);
  }

  return (
    <section className="w-full mb-6">
      <h2 className="text-xl mb-3">{t(msg.product.topUps)}</h2>
      {topUps?.map((i, idx) => (
        <TopUpRow key={idx} idx={idx} methods={methods} topUpSuggestions={topUpSuggestions} />
      ))}
      <Button onClick={addRow} type="button" id="add-topup-option-btn">
        {t(msg.product.addTopUp)} <PlusIcon className="w-4 h-4 stroke-white stroke-2 fill-white" />
      </Button>
    </section>
  );
}
interface TopRowProps {
  idx: number;
  methods: UseFormReturn<CustomProductData>;
  topUpSuggestions?: TopUpSuggestion[];
}

const TopUpRow = ({ idx, methods, topUpSuggestions }: TopRowProps) => {
  const { t, msg } = useTranslation();

  const suggestions = topUpSuggestions?.map((t) => ({ name: t.name, value: t }));

  const handleClick = (value: TopUpSuggestion) => {
    methods.setValue(`topUps.${idx}`, value);
  };

  return (
    <div className="bg-inherit w-full mb-2 flex flex-col items-center">
      <div className="flex w-full gap-2">
        <SearchInput
          methods={methods}
          fieldName={`topUps.${idx}.name`}
          placeholder={t(msg.product.topUps)}
          suggesstions={suggestions}
          handleClick={handleClick}
        />
        <label className="basis-1/3 shrink-0">
          <Input fieldName={`topUps.${idx}.price`} methods={methods} placeholder={t(msg.product.price2)} />
        </label>
      </div>
    </div>
  );
};

interface InputProps {
  methods: UseFormReturn<CustomProductData>;
  fieldName: FieldPath<CustomProductData>;
  registerOptions?: RegisterOptions<CustomProductData>;
  placeholder: string;
}

const Input = ({ methods, fieldName, registerOptions, placeholder }: InputProps) => {
  return (
    <input
      placeholder={placeholder}
      className="px-3 py-2 w-full rounded border border-[#CCCCCC] text-black dark:text-dark-gray-text dark:bg-pos-black-blue"
      {...methods.register(fieldName, registerOptions)}
    />
  );
};

interface SearchInputProps extends InputProps {
  handleClick: (v: string | TopUpSuggestion) => void;
  suggesstions?: { name: string; value: string | TopUpSuggestion }[];
}

function SearchInput({ methods, fieldName, registerOptions, placeholder, handleClick, suggesstions }: SearchInputProps) {
  const [focused, setFocused] = useState(false);
  const searchText = useWatch({ control: methods.control, name: fieldName });

  const matchList = suggesstions?.filter((s) => {
    return s.name?.toLowerCase().startsWith(searchText.toLowerCase());
  });

  const handleSelect = (v: string | TopUpSuggestion) => {
    setFocused(false);
    handleClick(v);
  };

  return (
    <label
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
      className="w-full"
    >
      <Input methods={methods} fieldName={fieldName} placeholder={placeholder} registerOptions={registerOptions} />
      <>
        {matchList && matchList?.length > 0 && focused ? (
          <div
            id="search-input-dropdown"
            className="w-full fixed top-[50px] left-0 md:absolute md:top-[unset] md:left-[unset] z-20 bg-blue-100 border overflow-y-scroll max-h-96"
          >
            <ul>
              {matchList.map((m) => (
                <li
                  key={m.name}
                  attr-name={m.name}
                  className="text-pos-blue cursor-pointer grid gap-4 lg:grid-flow-col lg:auto-cols-fr p-2 hover:bg-blue-200"
                  onMouseDown={() => handleSelect(m.value)}
                >
                  {m.name}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </>
    </label>
  );
}

interface SearchInputSuggestionValueProps extends InputProps {
  idx: number;
  handleClick: (v: string | TopUpSuggestion) => void;
  uniqueSuggestions?: { name: string; value: string | TopUpSuggestion }[];
  customSuggestions: CustomSuggestion[];
}

function SearchInputSuggestionValue({
  idx,
  methods,
  fieldName,
  registerOptions,
  placeholder,
  handleClick,
  uniqueSuggestions,
  customSuggestions,
}: SearchInputSuggestionValueProps) {
  const [focused, setFocused] = useState(false);
  const searchText = useWatch({ control: methods.control, name: fieldName });
  const options = useWatch({ control: methods.control, name: "options" });

  const matchList = customSuggestions
    .filter((item) => {
      for (let i = 0; i < idx; i++) {
        if (item.options[i].value !== options[i].value) return false;
      }
      return true;
    })
    .filter((v) => v.options[idx] !== undefined)
    .map((v) => ({ name: v.options[idx].value, value: v.options[idx].value }))
    .reduce<{ name: string; value: string }[]>((uniqueList, item) => {
      if (!uniqueList.some((item2) => item2.name === item.name)) {
        uniqueList.push(item);
      }
      return uniqueList;
    }, []);

  const handleSelect = (v: string | TopUpSuggestion) => {
    setFocused(false);
    handleClick(v);
  };

  return (
    <label
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
      className="w-full"
    >
      <Input methods={methods} fieldName={fieldName} placeholder={placeholder} registerOptions={registerOptions} />
      <>
        {matchList && matchList?.length > 0 && focused ? (
          <div id="search-input-dropdown" className="w-full absolute z-20 bg-blue-100 border">
            <ul>
              {matchList.map((m) => (
                <li
                  key={m.name}
                  attr-name={m.name}
                  className="text-pos-blue cursor-pointer grid gap-4 lg:grid-flow-col lg:auto-cols-fr p-2 hover:bg-blue-200"
                  onMouseDown={() => handleSelect(m.value)}
                >
                  {m.name}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </>
    </label>
  );
}

const Button = ({ type, children, isActive = true, ...rest }) => {
  return (
    <button
      data-submit-btn-active={isActive}
      {...rest}
      disabled={!isActive}
      type={type}
      className="text-white bg-blue-500 px-8 py-2 disabled:bg-her-gray broder-2 rounded-md w-full flex justify-center items-center gap-2"
    >
      {children}
    </button>
  );
};

function PlusIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}
